import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack, Group, Grid } from '@/components/ui/layout';
import { Title, Text, Button, Badge } from '@/components/ui/core';
import { TextInput, Switch } from '@/components/ui/inputs';
import { Alert } from '@/components/ui/feedback';
import { Stepper, Card as MantineCard, SegmentedControl } from '@mantine/core';
import { IconUser, IconPalette, IconSettings, IconAccessible, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { FadeIn } from '@/components/animations';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { usePreferencesStore } from '@/stores';
import { colorSchemes, getGradientStyle } from '@/theme/theme';
import { useHttpClient } from '@actschurch/ccms-sdk/react';
import { UserPreferencesService } from '@actschurch/ccms-sdk';

export default function OnboardingPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const { user } = useEnrichedAuth();
  const httpClient = useHttpClient();
  const navigate = useNavigate();
  const updatePreferences = usePreferencesStore(state => state.updatePreferences);

  // Step 1: Welcome & Display Name
  const [displayName, setDisplayName] = useState(user?.displayName || `${user?.firstName} ${user?.surname}`);

  // Get current preferences from store
  const storeColorSchemeKey = usePreferencesStore(state => state.colorSchemeKey);
  const storeColorSchemeMode = usePreferencesStore(state => state.colorSchemeMode);
  const storeFontSize = usePreferencesStore(state => state.fontSize);
  const storeUiSize = usePreferencesStore(state => state.uiSize);
  const storeHighContrast = usePreferencesStore(state => state.highContrast);
  const storeEnableTooltips = usePreferencesStore(state => state.enableTooltips);
  const storeStrongFocus = usePreferencesStore(state => state.strongFocus);
  const storeReducedMotion = usePreferencesStore(state => state.reducedMotion);

  // Step 2: Theme - initialize from store
  const [colorSchemeKey, setColorSchemeKey] = useState(storeColorSchemeKey || 'royal-indigo');
  const [colorSchemeMode, setColorSchemeMode] = useState<'light' | 'dark' | 'auto'>(storeColorSchemeMode || 'auto');

  // Step 3: UI Preferences - initialize from store
  const [fontSize, setFontSize] = useState<'xs' | 'sm' | 'regular' | 'lg' | 'xl'>(storeFontSize || 'regular');
  const [uiSize, setUiSize] = useState<'compact' | 'regular' | 'comfortable' | 'spacious'>(storeUiSize || 'regular');
  const [highContrast, setHighContrast] = useState(storeHighContrast || false);
  const [enableTooltips, setEnableTooltips] = useState(storeEnableTooltips !== false);

  // Step 4: Accessibility - initialize from store
  const [strongFocus, setStrongFocus] = useState(storeStrongFocus || false);
  const [reduceMotion, setReduceMotion] = useState(storeReducedMotion || false);

  // Handlers that update both local state AND the store for immediate effect
  const handleColorSchemeKeyChange = (value: string) => {
    setColorSchemeKey(value);
    updatePreferences({ colorSchemeKey: value });
  };

  const handleColorSchemeModeChange = (value: 'light' | 'dark' | 'auto') => {
    setColorSchemeMode(value);
    updatePreferences({ colorSchemeMode: value });
  };

  const handleFontSizeChange = (value: 'xs' | 'sm' | 'regular' | 'lg' | 'xl') => {
    setFontSize(value);
    updatePreferences({ fontSize: value });
  };

  const handleUiSizeChange = (value: 'compact' | 'regular' | 'comfortable' | 'spacious') => {
    setUiSize(value);
    updatePreferences({ uiSize: value });
  };

  const handleHighContrastChange = (value: boolean) => {
    setHighContrast(value);
    updatePreferences({ highContrast: value });
  };

  const handleEnableTooltipsChange = (value: boolean) => {
    setEnableTooltips(value);
    updatePreferences({ enableTooltips: value });
  };

  const handleStrongFocusChange = (value: boolean) => {
    setStrongFocus(value);
    updatePreferences({ strongFocus: value });
  };

  const handleReduceMotionChange = (value: boolean) => {
    setReduceMotion(value);
    updatePreferences({ reducedMotion: value });
  };

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 3));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 0));

  const handleComplete = async () => {
    console.log('[Onboarding] handleComplete called');
    console.log('[Onboarding] httpClient available:', !!httpClient);
    setIsLoading(true);
    setApiError('');

    try {
      // STEP 1: Prepare preferences data (includes onboardingCompleted and displayName)
      const prefsData = {
        colorSchemeKey,
        colorSchemeMode,
        fontSize,
        uiSize,
        highContrast,
        enableTooltips,
        strongFocus,
        reducedMotion: reduceMotion,
        onboardingCompleted: true,
        displayName: displayName.trim() || undefined,
      };
      console.log('[Onboarding] Preferences data:', prefsData);

      // STEP 2: Save to local Zustand store
      updatePreferences(prefsData);
      console.log('[Onboarding] Local store updated');

      // STEP 3: Save preferences to server (userId from token, appId=2 for ccms_vendors)
      const userPreferencesService = new UserPreferencesService(httpClient);
      const prefsJson = JSON.stringify(prefsData);
      console.log('[Onboarding] Attempting to save preferences to server...');

      try {
        // Try to update existing preferences
        console.log('[Onboarding] Checking for existing preferences...');
        const existing = await userPreferencesService.getMyPreferencesByApp('2');
        console.log('[Onboarding] Found existing preferences, updating...', existing);
        await userPreferencesService.updateMyPreferences(existing.id, {
          preferences: prefsJson,
        });
        console.log('[Onboarding] Preferences updated successfully');
      } catch (fetchError: any) {
        // No existing preferences - create new
        console.log('[Onboarding] No existing preferences found, creating new...', fetchError?.message);
        await userPreferencesService.createMyPreferences({
          appId: '2',
          preferences: prefsJson,
        });
        console.log('[Onboarding] Preferences created successfully');
      }

      // STEP 4: Update lastSyncTime in store
      updatePreferences({
        lastSyncTime: Math.floor(Date.now() / 1000),
      });

      // Navigate to main page
      console.log('[Onboarding] Navigating to home...');
      navigate('/');
    } catch (err: any) {
      console.error('[Onboarding] Error:', err);
      setApiError(err.message || err.response?.data?.message || 'Failed to save preferences. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipStep = () => {
    if (currentStep === 3) {
      handleComplete();
    } else {
      nextStep();
    }
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      <FadeIn delay={300}>
        <Stack align="center" gap="xs">
          <Badge
            variant="gradient"
            gradient={{ from: 'indigo', to: 'violet' }}
            size="lg"
          >
            Welcome to CCMS
          </Badge>
          <Title order={2} ta="center" mt="md">
            Let's personalize your experience
          </Title>
          <Text c="dimmed" size="sm" ta="center">
            Customize your workspace to match your preferences
          </Text>
        </Stack>
      </FadeIn>

      {/* Stepper */}
      <Stepper active={currentStep} size="sm" allowNextStepsSelect={false}>
        <Stepper.Step label="Welcome" icon={<IconUser size={18} />} />
        <Stepper.Step label="Theme" icon={<IconPalette size={18} />} />
        <Stepper.Step label="UI" icon={<IconSettings size={18} />} />
        <Stepper.Step label="Accessibility" icon={<IconAccessible size={18} />} />
      </Stepper>

      {/* Error Alert */}
      {apiError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
        >
          {apiError}
        </Alert>
      )}

      {/* Step Content */}
      <Stack gap="lg" mt="md">
        {/* Step 1: Welcome */}
        {currentStep === 0 && (
          <FadeIn key="step-0">
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                Welcome, {user?.firstName}! Let's set up your profile and preferences.
              </Text>

              <TextInput
                label="Display Name"
                placeholder="How should we call you?"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                description="This is how you'll appear to other users"
                size="md"
              />

              <Text size="xs" c="dimmed">
                You can always change these settings later from your profile.
              </Text>
            </Stack>
          </FadeIn>
        )}

        {/* Step 2: Theme */}
        {currentStep === 1 && (
          <FadeIn key="step-1">
            <Stack gap="md">
              <div>
                <Text size="sm" fw={500} mb="xs">Color Mode</Text>
                <SegmentedControl
                  value={colorSchemeMode}
                  onChange={(value: any) => handleColorSchemeModeChange(value)}
                  data={[
                    { label: 'Light', value: 'light' },
                    { label: 'Dark', value: 'dark' },
                    { label: 'Auto', value: 'auto' },
                  ]}
                  fullWidth
                />
              </div>

              <div>
                <Text size="sm" fw={500} mb="md">Color Scheme</Text>
                <Grid gutter="md">
                  {colorSchemes.map((scheme) => (
                    <Grid.Col key={scheme.key} span={{ base: 6, sm: 4, md: 3 }}>
                      <MantineCard
                        padding="sm"
                        radius="md"
                        withBorder
                        style={{
                          cursor: 'pointer',
                          borderColor: colorSchemeKey === scheme.key ? 'var(--mantine-color-indigo-6)' : undefined,
                          borderWidth: colorSchemeKey === scheme.key ? 2 : 1,
                        }}
                        onClick={() => handleColorSchemeKeyChange(scheme.key)}
                      >
                        <Stack gap="xs" align="center">
                          {/* Gradient swatches showing primary, secondary, tertiary colors */}
                          <Group gap={4} wrap="nowrap">
                            <div
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: 4,
                                background: getGradientStyle(scheme.primary),
                              }}
                            />
                            <div
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: 4,
                                background: getGradientStyle(scheme.secondary),
                              }}
                            />
                            <div
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: 4,
                                background: getGradientStyle(scheme.tertiary),
                              }}
                            />
                          </Group>
                          <Text size="xs" ta="center" fw={colorSchemeKey === scheme.key ? 600 : 400}>
                            {scheme.label}
                          </Text>
                          {colorSchemeKey === scheme.key && (
                            <IconCheck size={16} color="var(--mantine-color-indigo-6)" />
                          )}
                        </Stack>
                      </MantineCard>
                    </Grid.Col>
                  ))}
                </Grid>
              </div>
            </Stack>
          </FadeIn>
        )}

        {/* Step 3: UI Preferences */}
        {currentStep === 2 && (
          <FadeIn key="step-2">
            <Stack gap="md">
              <div>
                <Text size="sm" fw={500} mb="xs">Font Size</Text>
                <SegmentedControl
                  value={fontSize}
                  onChange={(value: any) => handleFontSizeChange(value)}
                  data={[
                    { label: 'XS', value: 'xs' },
                    { label: 'Small', value: 'sm' },
                    { label: 'Regular', value: 'regular' },
                    { label: 'Large', value: 'lg' },
                    { label: 'XL', value: 'xl' },
                  ]}
                  fullWidth
                />
              </div>

              <div>
                <Text size="sm" fw={500} mb="xs">UI Density</Text>
                <SegmentedControl
                  value={uiSize}
                  onChange={(value: any) => handleUiSizeChange(value)}
                  data={[
                    { label: 'Compact', value: 'compact' },
                    { label: 'Regular', value: 'regular' },
                    { label: 'Comfortable', value: 'comfortable' },
                    { label: 'Spacious', value: 'spacious' },
                  ]}
                  fullWidth
                />
              </div>

              <Group justify="space-between">
                <div>
                  <Text size="sm" fw={500}>High Contrast</Text>
                  <Text size="xs" c="dimmed">Increase color contrast for better visibility</Text>
                </div>
                <Switch
                  checked={highContrast}
                  onChange={(e) => handleHighContrastChange(e.currentTarget.checked)}
                />
              </Group>

              <Group justify="space-between">
                <div>
                  <Text size="sm" fw={500}>Enable Tooltips</Text>
                  <Text size="xs" c="dimmed">Show helpful hints on hover</Text>
                </div>
                <Switch
                  checked={enableTooltips}
                  onChange={(e) => handleEnableTooltipsChange(e.currentTarget.checked)}
                />
              </Group>
            </Stack>
          </FadeIn>
        )}

        {/* Step 4: Accessibility */}
        {currentStep === 3 && (
          <FadeIn key="step-3">
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                Configure accessibility features to match your needs
              </Text>

              <Group justify="space-between">
                <div>
                  <Text size="sm" fw={500}>Strong Focus Indicators</Text>
                  <Text size="xs" c="dimmed">Show prominent focus rings for keyboard navigation</Text>
                </div>
                <Switch
                  checked={strongFocus}
                  onChange={(e) => handleStrongFocusChange(e.currentTarget.checked)}
                />
              </Group>

              <Group justify="space-between">
                <div>
                  <Text size="sm" fw={500}>Reduce Motion</Text>
                  <Text size="xs" c="dimmed">Minimize animations and transitions</Text>
                </div>
                <Switch
                  checked={reduceMotion}
                  onChange={(e) => handleReduceMotionChange(e.currentTarget.checked)}
                />
              </Group>

              <Alert color="blue" alertVariant="light" mt="md">
                These settings help ensure the application is accessible to everyone. You can always adjust them later in Settings.
              </Alert>
            </Stack>
          </FadeIn>
        )}
      </Stack>

      {/* Navigation Buttons */}
      <Group justify="space-between" mt="xl">
        <Button
          variant="subtle"
          onClick={prevStep}
          disabled={currentStep === 0}
        >
          Back
        </Button>

        <Group gap="sm">
          <Button
            variant="subtle"
            onClick={handleSkipStep}
          >
            Skip
          </Button>

          {currentStep < 3 ? (
            <Button
              variant="gradient"
              gradient={{ from: 'indigo', to: 'violet' }}
              onClick={nextStep}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="gradient"
              gradient={{ from: 'indigo', to: 'violet' }}
              onClick={handleComplete}
              loading={isLoading}
            >
              Complete Setup
            </Button>
          )}
        </Group>
      </Group>
    </Stack>
  );
}
